import { useState, useEffect } from "react";
import { useTheme } from "../../context/ThemeContext";
import { useUser } from "../../context/UserContext";
import AddFundsModal from "./AddFundsModal";

const groups = [
  { key: "core", label: "Core Balance" },
  { key: "business", label: "Business" },
  { key: "rewards", label: "Rewards" },
];

function formatDateTime(isoString) {
  if (!isoString) return { date: "—", time: "" };
  const d = new Date(isoString);
  const date = d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const time = d.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return { date, time };
}

function WalletCard({ user, isDarkMode, setShowModal }) {
  const U = user?.USER ?? user ?? {};
  const W = user?.WALLET ?? {};

  const balance = parseFloat(W?.balance ?? 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const initials = (U?.name ?? "U").charAt(0).toUpperCase();
  const updated = formatDateTime(W?.updated_at);
  const statusLabel =
    (W?.status?.charAt(0).toUpperCase() ?? "") + (W?.status?.slice(1) ?? "—");
  const isActive = W?.status === "active";

  const light = {
    addFundsBg: "#800020",
    addFundsBorder: "#800020",
    addFundsText: "#ffff",
    card: "#fdf8f8",
    border: "#e2e8f0",
    avatar: "#EAF3DE",
    avBorder: "#97C459",
    avText: "#27500A",
    name: "#0f172a",
    badge: "#f0fdf4",
    badgeBorder: "#86efac",
    badgeText: "#166534",
    wlabel: "#94a3b8",
    balance: "#0f172a",
    divider: "#f1f5f9",
    metaLbl: "#94a3b8",
    metaVal: "#0f172a",
    metaSub: "#94a3b8",
    metaDivider: "#f1f5f9",
  };

  const dark = {
    addFundsBg: "#800020",
    addFundsBorder: "#800020",
    addFundsText: "#ffff",
    card: "#1e293b",
    border: "#334155",
    avatar: "#27500A",
    avBorder: "#3B6D11",
    avText: "#C0DD97",
    name: "#f1f5f9",
    badge: "#14532D",
    badgeBorder: "#166634",
    badgeText: "#4ade80",
    wlabel: "#64748b",
    balance: "#f1f5f9",
    divider: "#334155",
    metaLbl: "#64748b",
    metaVal: "#f1f5f9",
    metaSub: "#64748b",
    metaDivider: "#334155",
  };

  const c = isDarkMode ? dark : light;

  return (
    <div
      style={{
        background: c.card,
        border: `0.5px solid ${c.border}`,
        borderRadius: 14,
        padding: "10px 12px",
        marginBottom: 16,
      }}
    >
      {/* Top row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              background: c.avatar,
              border: `1.5px solid ${c.avBorder}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 13,
              fontWeight: 500,
              color: c.avText,
              flexShrink: 0,
            }}
          >
            {initials}
          </div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: c.name,
              textTransform: "capitalize",
            }}
          >
            {U?.name ?? "User"}
          </div>
        </div>


        {/* Status badge */}
        {/* Status badge */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              background: c.badge,
              border: `0.5px solid ${c.badgeBorder}`,
              borderRadius: 20,
              padding: "3px 8px",
              fontSize: 10,
              color: c.badgeText,
            }}
          >
            <div
              style={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                background: isActive ? "#22c55e" : "#f59e0b",
              }}
            />
            {statusLabel}
          </div>
          <button
            onClick={() => setShowModal(true)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              background: c.addFundsBg,
              border: `0.5px solid ${c.addFundsBorder}`,
              borderRadius: 20,
              padding: "3px 8px",
              fontSize: 10,
              color: c.addFundsText,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            <span style={{ fontSize: 12, lineHeight: 1 }}>+</span>
            Add Funds
          </button>

        </div>
      </div>

      {/* Balance */}
      <div
        style={{
          fontSize: 9,
          letterSpacing: "0.07em",
          textTransform: "uppercase",
          color: c.wlabel,
          marginBottom: 2,
        }}
      >
        Wallet Balance
      </div>
      <div
        style={{
          fontSize: 24,
          fontWeight: 500,
          color: c.balance,
          fontFamily: "'DM Mono', 'Fira Mono', monospace",
          letterSpacing: "-0.02em",
          marginBottom: 12,
        }}
      >
        ₹{balance}
      </div>


    </div>
  );
}

function DashboardCard({ card, isDarkMode, index }) {
  const [hovered, setHovered] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), index * 80);
    return () => clearTimeout(t);
  }, [index]);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative",
        borderRadius: 10,
        padding: "8px 10px",
        display: "flex",
        flexDirection: "column",
        gap: 3,
        background: isDarkMode
          ? hovered ? "rgba(255,255,255,0.07)" : "rgba(255,255,255,0.04)"
          : hovered ? "#ffffff" : "#f9f9fb",
        border: `0.5px solid ${hovered
          ? card.accent[0] + "55"
          : isDarkMode
            ? "rgba(255,255,255,0.09)"
            : "rgba(0,0,0,0.07)"
          }`,
        cursor: "pointer",
        transition: "all 0.22s ease",
        transform: visible
          ? hovered ? "translateY(-1px)" : "translateY(0)"
          : "translateY(8px)",
        opacity: visible ? 1 : 0,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0, left: 0, right: 0,
          height: 2,
          background: `linear-gradient(90deg, ${card.accent[0]}, ${card.accent[1]})`,
          borderRadius: "10px 10px 0 0",
          opacity: hovered ? 1 : 0.75,
          transition: "opacity 0.22s",
        }}
      />
      <span style={{ fontSize: 15 }}>{card.icon}</span>
      <div
        style={{
          fontSize: 9,
          fontWeight: 500,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: isDarkMode ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.38)",
        }}
      >
        {card.label}
      </div>
      <div
        style={{
          fontSize: 13,
          fontWeight: 500,
          color: isDarkMode ? "#ffffff" : "#0f0f0f",
          fontFamily: "'DM Mono', 'Fira Mono', monospace",
          letterSpacing: "-0.01em",
        }}
      >
        {card.value}
      </div>
    </div>
  );
}

export default function DashboardCards() {
  const { isDarkMode } = useTheme();
  const { user } = useUser();
  const U = user?.USER ?? {};
  const W = user?.WALLET ?? {};
  const [showModal, setShowModal] = useState(false);

  // Format currency with proper locale string
  const formatCurrency = (value) => {
    const num = parseFloat(value ?? 0);
    return num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  console.log("API USER DATA", JSON.stringify(U, null, 2));

  const cards = [
    { id: "stake", label: "My Shopping", value: `₹${formatCurrency(U?.my_shopping)}`, icon: "🔒", accent: ["#7C3AED", "#5B21B6"], group: "core" },
    { id: "income", label: "Total Income", value: `₹${formatCurrency(U?.total_income)}`, icon: "📈", accent: ["#059669", "#047857"], group: "core" },
    { id: "withdraw", label: "Total Withdraw", value: `₹${formatCurrency(U?.withdraw_amount)}`, icon: "💸", accent: ["#DC2626", "#B91C1C"], group: "core" },
    { id: "available", label: "Available Amount", value: `₹${formatCurrency(U?.available_amount)}`, icon: "💰", accent: ["#0284C7", "#0369A1"], group: "core" },
    { id: "direct", label: "Direct Business", value: `₹${formatCurrency(U?.direct_business)}`, icon: "🤝", accent: ["#DB2777", "#BE185D"], group: "business" },
    { id: "team", label: "Team Business", value: `₹${formatCurrency(U?.team_business)}`, icon: "👥", accent: ["#16A34A", "#15803D"], group: "business" },
    { id: "staking", label: "Shopping Reward", value: `₹${formatCurrency(U?.stake_income)}`, icon: "🪙", accent: ["#D97706", "#B45309"], group: "rewards" },
    { id: "rank", label: "Referral Reward", value: `₹${formatCurrency(U?.rank_reward)}`, icon: "🏆", accent: ["#7C3AED", "#5B21B6"], group: "rewards" },
    { id: "level", label: "Level Income", value: `₹${formatCurrency(U?.level_income)}`, icon: "📊", accent: ["#0891B2", "#0E7490"], group: "rewards" },
  ];

  return (
    <div style={{ fontFamily: "'DM Sans', 'Nunito', sans-serif" }}>

      <WalletCard user={user} isDarkMode={isDarkMode} setShowModal={setShowModal} />

      {groups.map((group) => {
        const groupCards = cards.filter((c) => c.group === group.key);
        return (
          <div key={group.key} style={{ marginBottom: 20 }}>
            <div
              style={{
                fontSize: 9,
                fontWeight: 500,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: isDarkMode ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.3)",
                marginBottom: 8,
                paddingLeft: 2,
              }}
            >
              {group.label}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                gap: 6,
              }}
            >
              {groupCards.map((card, i) => (
                <DashboardCard
                  key={card.id}
                  card={card}
                  isDarkMode={isDarkMode}
                  index={i + groups.findIndex((g) => g.key === group.key) * 3}
                />
              ))}
            </div>
          </div>
        );
      })}
      <AddFundsModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        walletBalance={formatCurrency(W?.balance)}
      />
    </div>
  );
}